import React, { useEffect, useMemo, useRef, useState } from 'react';
import { newPaymentKey, paymentCommandError } from '../lib/paymentCommands';
import { businessToday } from '../lib/period';
import { accountsAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const fmtINR = (v) => `\u20B9${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Bulk payment dialog — one shared payment_reference across N mark-paid
// callbacks. Idempotency + partial-failure retry handled by the backend.
export default function BulkPaymentDialog({ open, onClose, bills, onDone }) {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [date, setDate] = useState(businessToday());
  const [notes, setNotes] = useState('');
  const [checked, setChecked] = useState({});   // bill_id -> bool
  const [allocs, setAllocs] = useState({});     // bill_id -> string amount
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  // One command key per dialog session → retries never repeat completed bills.
  const keyRef = useRef(newPaymentKey());

  const eligible = useMemo(
    () => (bills || []).filter(
      (b) => b.duplicate_state !== 'HARD_DUPLICATE' &&
             ['Unpaid', 'Partial', 'Partially Paid'].includes(b.payment_status),
    ).sort((a, b) => (a.bill_date || '').localeCompare(b.bill_date || '')),
    [bills],
  );

  useEffect(() => {
    if (!open) return;
    setAmount(''); setReference(''); setMethod('BANK_TRANSFER');
    setDate(businessToday()); setNotes('');
    setChecked({}); setAllocs({}); setResults(null);
    keyRef.current = newPaymentKey();
  }, [open]);

  // FIFO auto-allocate when total amount changes or selection toggles.
  useEffect(() => {
    const bulk = Number(amount) || 0;
    if (bulk <= 0) { setAllocs({}); return; }
    let remaining = bulk;
    const next = {};
    for (const b of eligible) {
      if (!checked[b.bill_id]) continue;
      if (remaining <= 0) { next[b.bill_id] = '0'; continue; }
      const bal = Math.max(Number(b.total_amount || 0) - Number(b.paid_amount || 0), 0);
      const take = Math.min(bal, remaining);
      next[b.bill_id] = String(take);
      remaining = Math.max(remaining - take, 0);
    }
    setAllocs(next);
  }, [amount, checked, eligible]);

  const allocated = Object.entries(allocs)
    .filter(([id]) => checked[id])
    .reduce((s, [, v]) => s + (Number(v) || 0), 0);
  const bulk = Number(amount) || 0;
  const diff = bulk - allocated;
  const balanced = bulk > 0 && Math.abs(diff) < 0.01;
  const anySelected = Object.values(checked).some(Boolean);

  const submit = async () => {
    if (!reference.trim()) { toast.error('UTR / Payment reference is required'); return; }
    if (!balanced) { toast.error('Allocations must equal the bulk payment amount'); return; }
    if (!anySelected) { toast.error('Select at least one bill'); return; }

    const allocations = eligible
      .filter((b) => checked[b.bill_id] && Number(allocs[b.bill_id]) > 0)
      .map((b) => {
        const amt = Number(allocs[b.bill_id]);
        const bal = Math.max(Number(b.total_amount || 0) - Number(b.paid_amount || 0), 0);
        return {
          bill_id: b.bill_id,
          amount_paid: amt,
          payment_status: amt + 0.01 >= bal ? 'Paid' : 'Partial',
        };
      });

    setBusy(true);
    try {
      const res = await accountsAPI.bulkMarkPaid({
        payment_reference: reference.trim(),
        payment_date: date,
        payment_method: method,
        notes: notes || undefined,
        allocations,
      }, keyRef.current);
      setResults(res.data);
      const { succeeded, failed, skipped_idempotent } = res.data;
      if (failed === 0) {
        toast.success(`Bulk payment recorded · ${succeeded} bill${succeeded === 1 ? '' : 's'} · ${skipped_idempotent} idempotent skips`);
        onDone?.();
      } else {
        toast.error(`${failed} allocation${failed === 1 ? '' : 's'} not recorded. Retry from this dialog — completed bills won't be repeated; unknown outcomes go to Payment Commands.`);
      }
    } catch (err) {
      const e = paymentCommandError(err, 'Bulk payment failed');
      toast.error(`${e.title}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl" data-testid="bulk-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Bulk Payment</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Settle multiple bills against one UTR / bank transfer. Allocations must sum to the total; retries with the same reference are safe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <Label className="text-[11px]">Total Payment (₹)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="h-9 bg-background border-border/50" data-testid="bulk-total-input" />
            </div>
            <div>
              <Label className="text-[11px]">UTR / Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)}
                className="h-9 bg-background border-border/50" data-testid="bulk-reference-input" />
            </div>
            <div>
              <Label className="text-[11px]">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-9 bg-background border-border/50" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px]">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="h-9 bg-background border-border/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="CASH">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[11px]">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                className="h-9 bg-background border-border/50" />
            </div>
          </div>

          <div className="rounded border border-border/50 max-h-64 overflow-auto">
            {eligible.map((b) => {
              const bal = Math.max(Number(b.total_amount || 0) - Number(b.paid_amount || 0), 0);
              return (
                <label key={b.bill_id} className="flex items-center gap-2 px-3 py-2 border-b border-border/30 last:border-0 text-xs hover:bg-muted/30 cursor-pointer" data-testid={`bulk-bill-row-${b.bill_id}`}>
                  <input type="checkbox" checked={!!checked[b.bill_id]}
                    onChange={(e) => setChecked((c) => ({ ...c, [b.bill_id]: e.target.checked }))}
                    data-testid={`bulk-bill-check-${b.bill_id}`} />
                  <span className="font-mono w-24 shrink-0">{b.bill_number}</span>
                  <span className="text-muted-foreground w-24 shrink-0">{(b.bill_date || '').slice(0, 10)}</span>
                  <span className="font-mono ml-auto text-amber-300">{fmtINR(bal)}</span>
                  <Input type="number" step="0.01" value={allocs[b.bill_id] || ''}
                    onChange={(e) => setAllocs((a) => ({ ...a, [b.bill_id]: e.target.value }))}
                    disabled={!checked[b.bill_id]}
                    className="h-7 w-24 bg-background border-border/50 text-xs font-mono"
                    data-testid={`bulk-bill-alloc-${b.bill_id}`} />
                </label>
              );
            })}
            {eligible.length === 0 && (
              <p className="text-xs text-muted-foreground px-3 py-4 text-center">No unpaid bills</p>
            )}
          </div>

          <div className="flex items-center justify-between text-xs">
            <span>Allocated: <span className="font-mono">{fmtINR(allocated)}</span> · Total: <span className="font-mono">{fmtINR(bulk)}</span></span>
            <span className={`font-mono ${Math.abs(diff) < 0.01 ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="bulk-diff">
              Diff: {fmtINR(diff)}
            </span>
          </div>

          {results && (
            <div className="rounded border border-border/50 p-2 text-xs space-y-1 max-h-32 overflow-auto" data-testid="bulk-results">
              {results.results.map((r) => (
                <div key={r.bill_id} className="flex items-center gap-2">
                  {r.status === 'SUCCESS' && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                  {r.status === 'SKIPPED_IDEMPOTENT' && <CheckCircle2 className="w-3 h-3 text-sky-400" />}
                  {!['SUCCESS', 'SKIPPED_IDEMPOTENT'].includes(r.status) && <XCircle className="w-3 h-3 text-rose-400" />}
                  <span className="font-mono">{r.bill_id.slice(0, 8)}</span>
                  <span>{r.status}</span>
                  {r.reason && <span className="text-muted-foreground truncate">· {r.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={busy || !balanced || !anySelected}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bulk-submit-btn">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Confirm Bulk Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
